import type { ReactElement } from "react";
import {
  Alert,
  AsyncSection,
  asyncStatus,
  SettingsGroup,
  Skeleton,
} from "@playbookai/ui";
import { presentTeamRequest } from "./request-direction.js";
import { LoadMore } from "./LoadMore.js";
import { TabCount } from "./team-identity.js";
import { TeamRequestList, type QueuedRequest } from "./TeamRequestList.js";
import type { TeamRequestAction } from "../../api/teams-api.js";
import {
  useIncomingTeamRequests,
  useMyTeamRequests,
  useResolveTeamRequest,
} from "./useTeams.js";

/**
 * Requests & invitations — both directions, on one screen (ADR-0082).
 *
 * TWO SECTIONS, because they ask the reader different questions. "Waiting for
 * you" is somebody else's proposal that the reader DECIDES; "Waiting for
 * somebody else" is the reader's own, which they can only withdraw. Merging
 * them would put an Accept button and a Withdraw button in the same column
 * meaning opposite things.
 *
 * THE SPLIT IS BY DIRECTION, NOT BY QUEUE, and that distinction is what this
 * screen got wrong. `/requests/incoming` is scoped by OWNERSHIP: it returns
 * join requests somebody sent the reader AND invitations the reader sent out.
 * Treating the whole queue as "waiting for you" offered **Approve** on the
 * owner's own outgoing invitation — which the server refuses, correctly, with
 * "Only the invited person can answer this invitation". `presentTeamRequest`
 * is the one place the four cases are decided, shared with the team page so
 * the two cannot drift, and it mirrors `assertMayResolve` exactly.
 *
 * BOTH QUEUES PAGE. They are separate cursors, so each section carries its own
 * Load more — an owner of many teams has an unbounded incoming queue, and
 * showing the first fifty rows with nothing to say so would read as all of
 * them.
 */
/**
 * Both queues, split by DIRECTION. The Teams page reads it for the tab count
 * and the panel reads it for its two sections; the queries are shared, so the
 * split is computed from the same rows in both places.
 */
export function useTeamRequestQueues() {
  const mine = useMyTeamRequests("pending");
  const incoming = useIncomingTeamRequests();

  const rows: readonly QueuedRequest[] = [
    ...mine.items.map((row) => ({ queue: "mine" as const, row })),
    ...incoming.items.map((row) => ({ queue: "incoming" as const, row })),
  ];
  const decide = rows.filter(
    ({ queue, row }) => presentTeamRequest(queue, row).direction === "inbound",
  );
  const waiting = rows.filter(
    ({ queue, row }) => presentTeamRequest(queue, row).direction === "outbound",
  );
  /* EMPTINESS IS ONLY TRUE ONCE BOTH CURSORS ARE EXHAUSTED.
     Each queue mixes directions — the owner queue carries outbound invitations
     as well as the inbound join requests that need deciding — so a first page
     can be entirely outbound while actionable requests sit on page two. Saying
     "nothing is waiting for you" there is a false statement about work the
     reader still has, which is worse than saying nothing at all. */
  const morePages = mine.hasMore || incoming.hasMore;

  return {
    mine,
    incoming,
    decide,
    waiting,
    morePages,
    isLoading: mine.isLoading || incoming.isLoading,
    isError: mine.error !== null || incoming.error !== null,
  };
}

export function TeamRequestsPanel(): ReactElement {
  const { mine, incoming, decide, waiting, morePages, isLoading, isError } =
    useTeamRequestQueues();
  const resolve = useResolveTeamRequest();
  const onResolve = (requestId: string, action: TeamRequestAction): void =>
    resolve.mutate({ requestId, action });

  return (
    <div className="space-y-7.5">
      {resolve.isError && (
        <Alert role="status" tone="critical" title="That did not go through">
          {resolve.error instanceof Error
            ? resolve.error.message
            : "Reload and try again."}
        </Alert>
      )}

      <SettingsGroup
        variant="settings"
        title="Waiting for you"
        description="Invitations to teams, and requests to join the teams you own. You decide these."
        badge={
          decide.length > 0 ? (
            <TabCount
              value={`${decide.length}${morePages ? "+" : ""}`}
              attention
            />
          ) : null
        }
      >
        <AsyncSection
          className="mt-3"
          status={asyncStatus({
            isLoading,
            isError,
            isEmpty: decide.length === 0,
            hasData: decide.length > 0,
          })}
          errorTitle={"Your requests could not be loaded"}
          loadingLabel="your invitations and requests"
          skeleton={
            /* Kept hand-built: Card is the 14px corner with an 80% border
               and the top hairline, where this placeholder is 10px on
               today's full pb-border. */
            <div className="space-y-3 rounded-lg border border-pb-border bg-pb-card p-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-4/5" />
            </div>
          }
          errorDescription={
            "Invitations and requests are temporarily unavailable."
          }
          onRetry={() => {
            mine.refetch();
            incoming.refetch();
          }}
          empty={
            <TeamRequestList
              rows={[]}
              pending={false}
              onResolve={onResolve}
              emptyMessage={
                morePages ? (
                  <>
                    <strong className="font-medium text-pb-text">
                      Nothing waiting on the pages loaded so far
                    </strong>
                    {" — "}
                    more invitations and requests have not been loaded yet. Use
                    Load more below to see the rest.
                  </>
                ) : (
                  <>
                    <strong className="font-medium text-pb-text">
                      Nothing is waiting for you
                    </strong>
                    {" — "}
                    invitations to teams, and requests to join the teams you
                    own, appear here.
                  </>
                )
              }
            />
          }
        >
          <TeamRequestList
            rows={decide}
            pending={resolve.isPending}
            onResolve={onResolve}
            emptyMessage={null}
          />
        </AsyncSection>
      </SettingsGroup>

      <SettingsGroup
        variant="settings"
        title="Waiting for somebody else"
        description="Requests you made and invitations you sent. Only the other person can answer them; you can withdraw."
        badge={
          waiting.length > 0 ? (
            <TabCount value={`${waiting.length}${morePages ? "+" : ""}`} />
          ) : null
        }
      >
        {/* Not behind the section above's loading and error states twice:
            while the queues load there is nothing to say here, and a failure
            is already named once, with its Retry, above. */}
        {isLoading || isError ? null : (
          <div className="mt-3">
            <TeamRequestList
              rows={waiting}
              pending={resolve.isPending}
              onResolve={onResolve}
              emptyMessage={
                morePages
                  ? "Nothing outstanding on the pages loaded so far — use Load more below to see the rest."
                  : "You have nothing outstanding — no invitation you sent and no request you made is still waiting."
              }
            />
          </div>
        )}
      </SettingsGroup>

      {/* ONE control per QUEUE, under both sections, because a queue's next
          page can contain rows for either of them — a cursor is per request
          list, not per direction. */}
      <div className="flex flex-wrap gap-2">
        <LoadMore
          label="invitations and requests for you"
          hasMore={mine.hasMore}
          isLoadingMore={mine.isLoadingMore}
          failed={mine.loadMoreFailed}
          onLoadMore={mine.loadMore}
        />
        <LoadMore
          label="requests on the teams you own"
          hasMore={incoming.hasMore}
          isLoadingMore={incoming.isLoadingMore}
          failed={incoming.loadMoreFailed}
          onLoadMore={incoming.loadMore}
        />
      </div>
    </div>
  );
}
